import { useCallback, useRef, useState } from "react";
import { CloseConfirmationDialog } from "../CloseConfirmationDialog/CloseConfirmationDialog";

interface ExitConfirmationConfig {
  onSaveChanges: () => void;
  onExitWithoutSaving: () => void;
}

interface UseExitConfirmationResult {
  exitWithConfirmation: (config: ExitConfirmationConfig) => void;
  Component: React.FC;
}

export function useExitConfirmation(): UseExitConfirmationResult {
  const [isCloseConfirmationDialogOpen, setIsCloseConfirmationDialogOpen] =
    useState<boolean>(false);

  const configRef = useRef<ExitConfirmationConfig | null>(null);

  const exitWithConfirmation = useCallback((config: ExitConfirmationConfig) => {
    configRef.current = config;
    setIsCloseConfirmationDialogOpen(true);
  }, []);

  const handleConfirm = useCallback(() => {
    configRef.current?.onSaveChanges();
    setIsCloseConfirmationDialogOpen(false);
  }, []);

  const handleCancel = useCallback(() => {
    configRef.current?.onExitWithoutSaving();
    setIsCloseConfirmationDialogOpen(false);
  }, []);

  const handleBlur = useCallback(
    () => setIsCloseConfirmationDialogOpen(false),
    [],
  );

  const Component = useCallback<React.FC>(() => {
    if (!isCloseConfirmationDialogOpen) {
      return null;
    }

    return (
      <CloseConfirmationDialog
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        onBlur={handleBlur}
      />
    );
  }, [handleBlur, handleCancel, handleConfirm, isCloseConfirmationDialogOpen]);

  return {
    exitWithConfirmation,
    Component,
  };
}
